import { useState, useCallback, useMemo } from 'react';
import { usePost } from "../API";
import { useAsyncInterval } from '../Util';
import {
  AsyncJobPhase,
  isAsyncJobActive,
  isAsyncJobTerminal,
} from './AsyncJobPhase';

export type { AsyncJobPhase } from './AsyncJobPhase';
export {
  isAsyncJobActive,
  isAsyncJobFailure,
  isAsyncJobTerminal,
} from './AsyncJobPhase';

/**
 * Provides a way to watch the status of an async job.
 *
 * Example usage:
 * ```
  const queueJob = usePost('/some_queue_endpoint');
 * const { phase, setTaskId } = useAsyncJobStatus(3_000);
 *
 * useEffect(() => {
 *  const taskId = await queueJob({});
 *  setTaskId(taskId);
 * }, []);
 *
 * useEffect(() => {
 *  console.log('job phase:', phase);
 * }, [phase]);
 * ```
 *
 * Phase gets updated periodically, based on the interval you choose.
 *
 * @param intervalInMillis the interval, in milliseconds, to run the status check
 *
 */
export function useAsyncJobStatus(intervalInMillis: number = 5_000) {

  const checkStatus = usePost('/program/admin/async_job_status');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [phase, setPhase] = useState<AsyncJobPhase>('NOT_SUBMITTED');

  const doCheck = useCallback(async (taskId: string) => {

    const { status } = await checkStatus({ taskId });

    if (isAsyncJobTerminal(status)) {
      setShouldPoll(false);
    }

    if (status !== phase) {
      setPhase(status);
    }

  }, [taskId, shouldPoll, checkStatus, phase]);

  useAsyncInterval(async () => {
    if (!taskId || !shouldPoll) return;
    await doCheck(taskId);
  }, intervalInMillis);

  return {
    phase,
    taskId,
    setTaskId: useCallback((taskId: string) => {
      setShouldPoll(false);
      setTaskId(taskId);
      doCheck(taskId).then(() =>
        setShouldPoll(true)
      );
    }, [doCheck])
  };
}

export function useAsyncJobStatusBatch(intervalInMillis: number = 5_000) {
  const checkStatuses = usePost('/program/admin/async_job_statuses');
  const [taskIds, setTaskIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<Record<string, AsyncJobPhase>>({});

  const activeTaskIds = useMemo(() => (
    taskIds.filter((taskId) => isAsyncJobActive(statuses[taskId]))
  ), [taskIds, statuses]);

  const doCheck = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;

    try {
      const { statuses: newStatuses } = await checkStatuses({ taskIds: ids });

      setStatuses((prev) => {
        let didChange = false;
        const next = { ...prev };
        for (const taskId of ids) {
          const status = (newStatuses[taskId] ?? 'NOT_SUBMITTED') as AsyncJobPhase;
          if (next[taskId] !== status) {
            next[taskId] = status;
            didChange = true;
          }
        }
        return didChange ? next : prev;
      });
    } catch {
      // Keep existing statuses on error
    }
  }, [checkStatuses]);

  const addTaskIds = useCallback((ids: string[]) => {
    setTaskIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
    doCheck(ids);
  }, [doCheck]);

  const clearTaskIds = useCallback(() => {
    setTaskIds([]);
    setStatuses({});
  }, []);

  useAsyncInterval(async () => {
    if (activeTaskIds.length === 0) return;
    await doCheck(activeTaskIds);
  }, intervalInMillis);

  return {
    taskIds,
    activeTaskIds,
    statuses,
    hasActiveTasks: activeTaskIds.length > 0,
    addTaskIds,
    clearTaskIds,
  };
}
